import * as React from "react";
import { X, Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

/**
 * Multi-select component with tag-style display
 * Mobile-friendly with large tap targets
 */
export function MultiSelect({
  options = [],
  value = [],
  onChange,
  placeholder = "Select options...",
  className,
  disabled = false,
}) {
  const [open, setOpen] = React.useState(false);

  const handleSelect = (optionValue) => {
    if (disabled) return;
    
    const newValue = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];
    
    onChange(newValue);
  };

  const handleRemove = (optionValue, e) => {
    e.stopPropagation();
    if (disabled) return;
    onChange(value.filter((v) => v !== optionValue));
  };

  const selectedOptions = options.filter((opt) => value.includes(opt.value));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between min-h-[40px] h-auto py-2 px-3",
            "bg-white/5 border-white/10 text-white hover:bg-white/10",
            "focus:ring-2 focus:ring-[#D4AF37]/50",
            className
          )}
          data-testid="multi-select-trigger"
        >
          <div className="flex flex-wrap gap-1.5 flex-1">
            {selectedOptions.length === 0 ? (
              <span className="text-[#A1A1AA]">{placeholder}</span>
            ) : (
              selectedOptions.map((opt) => (
                <Badge
                  key={opt.value}
                  variant="secondary"
                  className={cn(
                    "px-2 py-0.5 text-xs font-medium rounded-sm",
                    opt.color || "bg-[#D4AF37] text-black"
                  )}
                >
                  {opt.label}
                  <button
                    type="button"
                    onClick={(e) => handleRemove(opt.value, e)}
                    className="ml-1.5 hover:bg-black/20 rounded-full p-0.5"
                    data-testid={`remove-tag-${opt.value}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))
            )}
          </div>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-full min-w-[200px] p-0 bg-[#27272a] border-white/10"
        align="start"
      >
        <div className="max-h-60 overflow-y-auto">
          {options.map((option) => {
            const isSelected = value.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelect(option.value)}
                className={cn(
                  "w-full flex items-center gap-2 px-3 py-2.5 text-left",
                  "hover:bg-white/10 transition-colors",
                  "text-white text-sm",
                  isSelected && "bg-[#D4AF37]/20"
                )}
                data-testid={`multi-select-option-${option.value}`}
              >
                <div
                  className={cn(
                    "w-4 h-4 border rounded-sm flex items-center justify-center",
                    isSelected
                      ? "bg-[#D4AF37] border-[#D4AF37]"
                      : "border-white/30"
                  )}
                >
                  {isSelected && <Check className="w-3 h-3 text-black" />}
                </div>
                <span className="flex-1">{option.label}</span>
                {option.color && (
                  <span
                    className={cn(
                      "w-3 h-3 rounded-full",
                      option.color.replace("text-", "bg-")
                    )}
                  />
                )}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default MultiSelect;
